import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { localize } from '@oneflowab/pomes';
import type { MessageTranslator } from '@oneflowab/pomes';
import { hasDraftApprovalFlow, isDraftApprovalFlowRunning } from '@flow/libs/agreement';
import agreementsReducer from '@app/reducers/entities/agreements';

import { TYPE_IS_INTERNAL_APPROVER } from '@flow/libs/agreement/participant/constants';
import {
  getRunningInternalApproversBlockIndex,
  getMaxAvailableBlockIndex,
} from '@flow/libs/agreement/draft-approval-flow';
import {
  Select,
  SelectItem,
  SelectContent,
  SelectTrigger,
  SelectValue,
  SelectIcon,
} from '@app/components/select-compound';

import style from './participant-approve-order.module.scss';

export type Props = {
  participant: Participant;
  agreement: Agreement;
  isEditable: boolean;
  approveOrder: number;
  message: MessageTranslator;
};

const ParticipantApproveOrderComponent: React.FC<Props> = ({
  participant,
  agreement,
  isEditable,
  approveOrder,
  message,
}) => {
  const [selected, setSelected] = useState<number>(approveOrder - 1);
  const dispatch = useDispatch();

  useEffect(() => {
    (async () => {
      setSelected(approveOrder - 1);
    })();
  }, [
    approveOrder,
  ]);

  if (!isEditable) {
    return null;
  }

  const getCurrentApproverOrder = () =>
    isDraftApprovalFlowRunning(agreement)
      ? getRunningInternalApproversBlockIndex(agreement) + 1
      : 0;

  const getApproverOrderOptions = () => {
    const maxApproveOrderOption = getMaxAvailableBlockIndex(agreement) + 1;
    const currentApproverOrder = getCurrentApproverOrder();

    return Array.from(
      {
        length: maxApproveOrderOption,
      },
      (_, i) => ({
        label: i + 1,
        value: i,
      }),
    ).filter((option) => option.label >= currentApproverOrder);
  };

  const updateParticipant = (params) =>
    dispatch(
      agreementsReducer.updateParticipant({
        id: participant.id,
        data: {
          ...params,
        },
      }),
    );

  const onApproverOrderChange = (value: number) => {
    const flowId = hasDraftApprovalFlow(agreement) ? agreement.draftApprovalFlow.id : null;
    const blockIndex = value || 0;
    updateParticipant({
      agreement: agreement.id,
      type: TYPE_IS_INTERNAL_APPROVER,
      flowId,
      blockIndex,
    });
  };

  const onChange = (value) => {
    setSelected(value);
    onApproverOrderChange(value);
  };

  const renderEditable = () => (
    <Select
      defaultValue={selected}
      onValueChange={onChange}
      variant="number"
    >
      <SelectTrigger
        id="approveOrder"
        aria-label={
          message({
            id: 'Select approve order',
            comment: 'Accessibility label for the approve order select input',
          }) as string
        }
      >
        <SelectValue />
        <SelectIcon />
      </SelectTrigger>
      <SelectContent>
        {getApproverOrderOptions().map((option) => (
          <SelectItem
            key={option.value}
            value={option.value}
          >
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  return <div className={style.ParticipantApproveOrder}>{renderEditable()}</div>;
};

export default localize(ParticipantApproveOrderComponent);
